import { makeAutoObservable } from "mobx";
import axios from 'axios';
import { getAPIUrl } from '../api/apiUtils';
import { session_job_types, dynamic_session_job_types } from '../utils/sessions';
import { formatRuDateTime } from '../utils/ruDateUtils';

class SessionsStore {
  // Состояние данных
  sessions = session_job_types.reduce((acc, job_type) => ({ ...acc, [job_type]: [] }), {});
  isLoading = false;
  error = null;
  lastFetchTime = null;
  
  // Константы
  CACHE_TTL = 5 * 60 * 1000; // 5 минут в миллисекундах
  API_ENDPOINT = '/monitoring/sessions/completed'; // Используем старый эндпоинт для совместимости

  constructor() {
    makeAutoObservable(this);
  }

  // Actions
  // на вход принимает плоский массив сессий и добавляет в store по типу сессии
  setSessions(sessions) {
    if (!Array.isArray(sessions)) {
      throw new Error('Sessions must be an array');
    }
    if (sessions.length === 0) {
      this.sessions = session_job_types.reduce((acc, job_type) => ({
        ...acc,
        [job_type]: []
      }), {});
    } else {
      this.sessions = session_job_types.reduce((acc, job_type) => ({
        ...acc,
        [job_type]: sessions.filter(session => session.job_type === job_type)
      }), {});
    }
    this.lastFetchTime = Date.now();
    this.error = null;
  }

  setIsLoading(loading) {
    this.isLoading = loading;
  }

  setError(error) {
    this.error = error;
    this.isLoading = false;
  }

  // Computed values
  get hasData() {
    return this.sessions.length > 0;
  }

  get isDataStale() {
    return !this.lastFetchTime || (Date.now() - this.lastFetchTime) > this.CACHE_TTL;
  }

  get shouldFetch() {
    return !this.hasData || this.isDataStale;
  }

  // Методы для работы с данными
  getSessionById(sessionId) {
    const sessionIdStr = String(sessionId);
    return this.sessions.find(session => String(session.session_id) === sessionIdStr);
  }

  // Основной метод загрузки данных
  async fetchSessions(forceRefresh = false) {
    // Если данные актуальны и не требуется принудительное обновление
    if (!forceRefresh && !this.shouldFetch) {
      return this.sessions;
    }

    // Если уже загружаем, не делаем повторный запрос
    if (this.isLoading) {
      return this.sessions;
    }

    this.setIsLoading(true);
    this.setError(null);

    try {
      const params = {
        limit: 10000000
      };
      const response = await axios.get(`${getAPIUrl()}${this.API_ENDPOINT}`, { params });
      
      if (Array.isArray(response.data)) {
        this.setSessions(response.data);
        return response.data;
      } else {
        throw new Error('Неверный формат данных от сервера');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Ошибка при загрузке сессий';
      this.setError(errorMessage);
      
      // Если это первая загрузка и произошла ошибка, возвращаем пустой массив
      if (!this.hasData) {
        this.setSessions([]);
      }
      
      throw error;
    } finally {
      this.setIsLoading(false);
    }
  }

  // Метод для принудительного обновления данных
  async refreshSessions() {
    return this.fetchSessions(true);
  }

  // Метод для очистки кэша
  clearCache() {
    this.lastFetchTime = null;
  }

  // Метод для сброса состояния
  reset() {
    this.sessions = [];
    this.isLoading = false;
    this.error = null;
    this.lastFetchTime = null;
  }

  // Валидация структуры данных
  validateSessionData(data) {
    if (!Array.isArray(data)) return false;
    
    return data.every(session => 
      session && 
      typeof session.session_id === 'number' &&
      typeof session.job_type === 'string' &&
      typeof session.group_id === 'string' &&
      typeof session.group_name === 'string' &&
      typeof session.meta_id === 'string' &&
      typeof session.start_date === 'string' &&
      typeof session.end_date === 'string' &&
      typeof session.created_at === 'string' &&
      typeof session.status === 'number'
    );
  }

  // Метод для получения статистики
  getStats() {
    const totalSessions = this.sessions.length;
    return {
      totalSessions,
      lastUpdate: this.lastFetchTime ? formatRuDateTime(this.lastFetchTime) : 'Никогда'
    };
  }
}

export default new SessionsStore();
